import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ContentChild,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router, RouterModule } from '@angular/router';
import { AbbCardComponent } from '@components/abb-card/abb-card.component';
import { TranslateService } from '@ngx-translate/core';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { CognitoService } from '@services/cognito/cognito.service';
import { ToastService } from '@services/ui/toast.service';
import { AbbCommonModule } from 'app/abb-common.module';
import { Subscription } from 'rxjs';

@Component({
  selector: 'abb-wizard-card-stepper',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    MatCardModule,
    TPipe,
    AbbCommonModule,
    FormsModule,
    MatCheckboxModule,
    AbbCardComponent,
  ],
  template: `
    <abb-card class="!py-0"
      [cardTitle]="('Step' | t) + ' ' + (currentStepIndex()+1) + '. ' + (steps()?.[currentStepIndex()]?.title | t)"
      [cardSubtitle]="(steps()?.[currentStepIndex()]?.subtitle | t) ?? undefined"
      [cardWidth]="'w-fit'"
      [cardHeight]="'h-auto'"
    >
      <ng-container card-content>
        <ng-container
          *ngTemplateOutlet="
            stepContent;
            context: { index: currentStepIndex() }
          "
        ></ng-container>
      </ng-container>
      <ng-container card-actions>
        @if(currentStepIndex()!=lastStepIndex()) {
        <div
          class="w-full flex flex-row justify-center"
          [ngClass]="{ 'gap-8': showBackBtn() && !hideNextStepBtn() }"
        >
          @if(showBackBtn()){
          <abb-button
            [id]="'next-btn'"
            variant="clean"
            type="button"
            [disabled]="nextStepBtnLoading()"
            (click)="goBack()"
            >{{ 'back' | t }}</abb-button
          >
          } @if(!hideNextStepBtn()){
          <abb-button
            [id]="'next-btn'"
            (click)="goNext()"
            [disabled]="nextStepBtnDisabled() || nextStepBtnLoading()"
            [loading]="nextStepBtnLoading()"
          >
            {{ 'Next Step' | t }}
            {{ (nextStepIndex() && currentStepIndex() === 2) ? '. '+(steps()?.[nextStepIndex()!]?.title | t) : ''
            }}
          </abb-button>
          }
        </div>
        } @else {
        <ng-container *ngTemplateOutlet="lastStepBtn"></ng-container>
        }
      </ng-container>
    </abb-card>
  `,
  styles: `
        .bg-white-field {
            --mdc-filled-text-field-container-color: var(--abb-color-white);
            --mat-form-field-state-layer-color: transparent;
        }
    `,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'wizard_first_access',
    },
  ],
  host: { class: 'block w-full h-full flex-1' },
})
export class WizardCardStepperComponent implements OnDestroy {
  @ContentChild('stepContent') stepContent!: TemplateRef<any>;
  @ContentChild('lastStepBtn') lastStepBtn!: TemplateRef<any>;
  steps = input.required<{ title: string; subtitle?: string }[] | undefined>();
  changeStep = output<number>();
  hideNextStepBtn = input<boolean>(false);
  nextStepBtnDisabled = input<boolean>(false);
  nextStepBtnLoading = input<boolean>(false);
  showBackBtn = input<boolean>(false);
  onBeforeGoNextStep = input<(() => Promise<boolean>) | undefined>();
  currentStepIndex = signal(0);
  nextStepIndex = computed(() => {
    const idx = this.currentStepIndex() + 1;
    const stepsCount = this.steps()?.length;
    if (stepsCount && idx <= stepsCount) {
      return idx;
    }
    return undefined;
  });
  previousStepIndex = computed(() => {
    const idx = this.currentStepIndex() - 1;
    if (idx > 0) {
      return idx;
    }
    return undefined;
  });
  lastStepIndex = computed(() => (this.steps()?.length ?? 1) - 1);
  private subscriptions: Subscription[] = [];
  constructor(
    private toast: ToastService,
    private cognitoService: CognitoService,
    private router: Router,
    private translate: TranslateService
  ) {}

  onGoBack = input<(() => void) | undefined>();
  goBack(): void {
    const onGoBack = this.onGoBack();
    if (onGoBack) {
      onGoBack();
    } else {
      if (this.previousStepIndex()) {
        this.changeStep.emit(
          this.previousStepIndex() ?? this.currentStepIndex()
        );
        this.currentStepIndex.update((prev) => prev - 1);
      }
    }
  }

  async goNext(): Promise<void> {
    if (this.nextStepIndex()) {
      const onBeforeGoNextStep = this.onBeforeGoNextStep();
      if (onBeforeGoNextStep) {
        const canContinue = await onBeforeGoNextStep();
        if (!canContinue) {
          return;
        }
      }
      this.changeStep.emit(this.nextStepIndex() ?? this.currentStepIndex());
      this.currentStepIndex.update((prev) => prev + 1);
    }
  }

  goToStep(stepIdx: number): void {
    if (stepIdx >= 0 && stepIdx <= this.lastStepIndex()) {
      this.changeStep.emit(stepIdx);
      this.currentStepIndex.set(stepIdx);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
